import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Check, ChevronRight, ChevronsDownUp, ChevronsUpDown, Copy, Search } from "lucide-react";
import { cn } from "../utils/cx.js";
import {
  childrenOf,
  containerPaths,
  jsonKind,
  matchRanges,
  pathKey,
  searchJson,
  type JsonPath,
} from "../utils/json.js";
import { Button } from "./Button.js";
import { Input } from "./Input.js";
import { Segmented } from "./Segmented.js";

export interface JsonViewerLabels {
  search: string;
  /** Accessible name of the tree/raw switch. */
  mode: string;
  tree: string;
  raw: string;
  expandAll: string;
  collapseAll: string;
  expand: string;
  collapse: string;
  copy: string;
  copied: string;
  /** `{count}` is replaced with the number of matching nodes. */
  matches: string;
  noMatches: string;
}

// Turkish fallbacks, like the rest of @app/ui ("Kapat", "Kopyala"); apps pass localized labels.
const DEFAULT_LABELS: JsonViewerLabels = {
  search: "JSON içinde ara",
  mode: "Görünüm modu",
  tree: "Ağaç",
  raw: "Ham",
  expandAll: "Tümünü genişlet",
  collapseAll: "Tümünü daralt",
  expand: "Genişlet",
  collapse: "Daralt",
  copy: "Kopyala",
  copied: "Kopyalandı",
  matches: "{count} eşleşme",
  noMatches: "Eşleşme yok",
};

export interface JsonViewerProps {
  /** Parsed JSON (e.g. `JSON.parse` output). Keep it referentially stable — a new value resets the tree. */
  value: unknown;
  /** Containers shallower than this start open (the root is depth 0). */
  defaultExpandDepth?: number;
  /** Height cap for the scrolling body; the toolbar stays above it. */
  maxHeight?: number | string;
  labels?: Partial<JsonViewerLabels>;
  className?: string;
}

type Mode = "tree" | "raw";

/** Only plain web links become anchors — never javascript:, data: or any other scheme. */
const LINK = /^https?:\/\/[^\s"<>]+$/;

const KIND_CLASS = {
  string: "text-code-string",
  number: "text-code-number",
  boolean: "text-code-literal",
  null: "text-code-literal",
} as const;

/** One indent step; the toggle column (`w-5`/`size-5`) is the same width. */
const STEP_REM = 1.25;

/** `text` with every occurrence of `query` wrapped in <mark>. */
function Highlight({ text, query }: { text: string; query: string }) {
  const ranges = matchRanges(text, query);
  if (ranges.length === 0) return <>{text}</>;
  const parts: ReactNode[] = [];
  let at = 0;
  for (const [start, end] of ranges) {
    if (start > at) parts.push(text.slice(at, start));
    parts.push(
      <mark
        key={start}
        className="rounded-sm bg-[var(--az-warning-bg)] text-inherit ring-1 ring-[var(--az-warning-border)]"
      >
        {text.slice(start, end)}
      </mark>,
    );
    at = end;
  }
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
}

function Row({ depth, lead, children }: { depth: number; lead?: ReactNode; children: ReactNode }) {
  return (
    <div
      className="flex items-start rounded-sm hover:bg-accent/60"
      style={{ paddingInlineStart: `${depth * STEP_REM}rem` }}
    >
      {lead ?? <span className="w-5 shrink-0" aria-hidden />}
      <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
    </div>
  );
}

interface NodeContext {
  open: Set<string>;
  toggle: (key: string) => void;
  hits: Set<string>;
  query: string;
  labels: JsonViewerLabels;
}

function JsonNode({
  name,
  value,
  path,
  depth,
  ctx,
}: {
  name?: string | number;
  value: unknown;
  path: JsonPath;
  depth: number;
  ctx: NodeContext;
}) {
  const key = pathKey(path);
  const kind = jsonKind(value);
  // Only a hit pays for the per-character highlighter; every other row renders plain.
  const query = ctx.hits.has(key) ? ctx.query : "";

  const label =
    name === undefined ? null : typeof name === "number" ? (
      <span className="text-muted-foreground">{name}: </span>
    ) : (
      <>
        <span className="text-code-key">
          &quot;
          <Highlight text={name} query={query} />
          &quot;
        </span>
        <span className="text-muted-foreground">: </span>
      </>
    );

  if (kind !== "object" && kind !== "array") {
    const text = String(value);
    const shown = <Highlight text={text} query={query} />;
    const inner =
      kind === "string" && LINK.test(text) ? (
        <a
          href={text}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
        >
          {shown}
        </a>
      ) : (
        shown
      );
    return (
      <Row depth={depth}>
        {label}
        <span className={KIND_CLASS[kind]}>{kind === "string" ? <>&quot;{inner}&quot;</> : inner}</span>
      </Row>
    );
  }

  const children = childrenOf(value);
  const [openBracket, closeBracket] = kind === "array" ? ["[", "]"] : ["{", "}"];
  if (children.length === 0) {
    return (
      <Row depth={depth}>
        {label}
        <span className="text-muted-foreground">
          {openBracket}
          {closeBracket}
        </span>
      </Row>
    );
  }

  const isOpen = ctx.open.has(key);
  const toggleLabel = `${isOpen ? ctx.labels.collapse : ctx.labels.expand}${name === undefined ? "" : ` ${name}`}`;
  return (
    <>
      <Row
        depth={depth}
        lead={
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={toggleLabel}
            onClick={() => ctx.toggle(key)}
            className="flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight className={cn("size-3.5 transition-transform", isOpen && "rotate-90")} />
          </button>
        }
      >
        {label}
        <span className="text-muted-foreground">
          {openBracket}
          {!isOpen && `…${closeBracket}`}
        </span>
        {/* Collapsed, the child count is the only hint of what is inside. */}
        {!isOpen && <span className="ms-2 text-muted-foreground/70 tabular-nums">{children.length}</span>}
      </Row>
      {isOpen && (
        <>
          {children.map(([childKey, child]) => (
            <JsonNode
              key={String(childKey)}
              name={childKey}
              value={child}
              path={[...path, childKey]}
              depth={depth + 1}
              ctx={ctx}
            />
          ))}
          <Row depth={depth}>
            <span className="text-muted-foreground">{closeBracket}</span>
          </Row>
        </>
      )}
    </>
  );
}

/**
 * Read-only JSON inspector: a collapsible, syntax-colored tree or the raw
 * pretty-printed text, with Turkish-aware search (hits are highlighted and their
 * containers opened) and copy. Rendering only — fetching, downloading and any
 * audit trail stay with the caller.
 */
export function JsonViewer({
  value,
  defaultExpandDepth = 2,
  maxHeight = "60vh",
  labels: labelOverrides,
  className,
}: JsonViewerProps) {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };
  const [mode, setMode] = useState<Mode>("tree");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(() => containerPaths(value, defaultExpandDepth));
  const [copied, setCopied] = useState(false);

  // A different document starts again from the default depth.
  useEffect(() => {
    setOpen(containerPaths(value, defaultExpandDepth));
  }, [value, defaultExpandDepth]);

  const search = useMemo(() => searchJson(value, query), [value, query]);

  // Reveal every hit by opening the containers above it. Merged, not replaced,
  // so what the user opened stays open — and they can still close these again.
  useEffect(() => {
    if (search.ancestors.size === 0) return;
    setOpen((prev) => new Set([...prev, ...search.ancestors]));
  }, [search]);

  const text = useMemo(() => JSON.stringify(value, null, 2) ?? "", [value]);
  const searching = query.trim() !== "";
  const hitCount = search.hits.size;

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const copy = () => {
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  };

  return (
    <div className={cn("flex min-w-0 flex-col gap-3", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          size="sm"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={labels.search}
          aria-label={labels.search}
          prefix={<Search className="size-3.5" />}
          className="w-full sm:w-64"
        />
        <span className="text-xs text-muted-foreground" aria-live="polite">
          {searching &&
            (hitCount === 0 ? labels.noMatches : labels.matches.replace("{count}", String(hitCount)))}
        </span>
        <div className="ms-auto flex items-center gap-1">
          {mode === "tree" && (
            <>
              <Button
                size="sm"
                variant="subtle"
                icon={<ChevronsUpDown />}
                title={labels.expandAll}
                aria-label={labels.expandAll}
                onClick={() => setOpen(containerPaths(value, Infinity))}
              />
              <Button
                size="sm"
                variant="subtle"
                icon={<ChevronsDownUp />}
                title={labels.collapseAll}
                aria-label={labels.collapseAll}
                // Depth 1 keeps the root open: a lone "{…}" tells the reader nothing.
                onClick={() => setOpen(containerPaths(value, 1))}
              />
            </>
          )}
          <Segmented<Mode>
            size="sm"
            label={labels.mode}
            value={mode}
            onChange={setMode}
            options={[
              { value: "tree", label: labels.tree },
              { value: "raw", label: labels.raw },
            ]}
          />
          <Button size="sm" icon={copied ? <Check /> : <Copy />} onClick={copy}>
            {copied ? labels.copied : labels.copy}
          </Button>
        </div>
      </div>

      {/* JSON reads left-to-right even in an RTL locale. Focusable so the raw view
          scrolls from the keyboard too. */}
      <div
        dir="ltr"
        role="region"
        aria-label={mode === "tree" ? labels.tree : labels.raw}
        tabIndex={0}
        style={{ maxHeight }}
        className="min-h-0 overflow-auto rounded-lg border border-border bg-muted/40 p-3 font-mono text-xs leading-5 text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {mode === "tree" ? (
          <JsonNode
            value={value}
            path={[]}
            depth={0}
            ctx={{ open, toggle, hits: search.hits, query, labels }}
          />
        ) : (
          <pre className="m-0 font-mono whitespace-pre-wrap [overflow-wrap:anywhere]">
            <Highlight text={text} query={query} />
          </pre>
        )}
      </div>
    </div>
  );
}
